import {
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";

import {
	AuthContext,
	type AuthContextValue,
	type AuthStatus,
} from "./auth-context-value";
import { authService } from "./auth.service";
import type {
	AuthUser,
	ChangePasswordPayload,
	LoginCredentials,
} from "./types";

export function AuthProvider({ children }: { children: ReactNode }) {
	const [user, setUser] = useState<AuthUser | null>(null);
	const [status, setStatus] = useState<AuthStatus>("loading");

	const refresh = useCallback(async () => {
		let current: AuthUser | null = null;
		try {
			current = await authService.me();
		} catch {
			current = null;
		}
		setUser(current);
		setStatus(current ? "authenticated" : "unauthenticated");
	}, []);

	useEffect(() => {
		void refresh();
	}, [refresh]);

	const login = useCallback(async (credentials: LoginCredentials) => {
		const next = await authService.login(credentials);
		setUser(next);
		setStatus("authenticated");
		return next;
	}, []);

	const logout = useCallback(async () => {
		await authService.logout();
		setUser(null);
		setStatus("unauthenticated");
	}, []);

	const changePassword = useCallback(async (payload: ChangePasswordPayload) => {
		const next = await authService.changePassword(payload);
		setUser(next);
		setStatus("authenticated");
		return next;
	}, []);

	const value = useMemo<AuthContextValue>(
		() => ({ status, user, login, logout, changePassword, refresh }),
		[status, user, login, logout, changePassword, refresh]
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
